/**
 * BATCH 22 PART 5 — Workflows 51-63 (Klaviyo)
 * 13 marketing workflow templates: winback, sunset, birthday, anniversary,
 * VIP, loyalty, wishlist, collection launch, gift guide, predictive reorder,
 * referral, coupon expiry, browse-to-cart conversion.
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 51-63 ============

export const ELITE_BATCH22_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 51. Winback Flow (Pattern G) ──────────────────────────────────
  "Winback Flow": {
    nodes: [
      trigger("Klaviyo: 90-day inactivity threshold", "schedule", {
        source: "klaviyo",
        schedule: "Daily at 9:00 AM UTC",
        howItStarts:
          "Runs daily to identify Klaviyo profiles that have not opened, clicked, or purchased in the last 90 days",
        setup:
          "Connect Klaviyo account. Create a segment of profiles with last_active_date older than 90 days. Schedule fires daily to pull newly qualifying profiles.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Klaviyo: Fetch profile activity and purchase history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.last_order_date",
                customName: "last_order_date",
                type: "string",
              },
              {
                name: "data.attributes.properties.lifetime_value",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "data.attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze inactivity reason and craft winback angle",
        520,
        "You are a retention marketing strategist. Given a lapsed customer profile, determine the most likely reason for inactivity and suggest a personalized winback angle. Return JSON with fields: inactivity_reason, winback_subject, winback_message, recommended_incentive.",
        "Customer: {first_name}, last order: {last_order_date}, LTV: ${lifetime_value}, total orders: {total_orders}. They have not engaged in 90+ days.",
        [
          {
            field: "inactivity_reason",
            as: "inactivity_reason",
            type: "string",
          },
          { field: "winback_subject", as: "winback_subject", type: "string" },
          { field: "winback_message", as: "winback_message", type: "string" },
          {
            field: "recommended_incentive",
            as: "recommended_incentive",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Check: Lifetime value above $200?", 760, {
        conditionDetails: {
          conditions: [
            { field: "lifetime_value", operator: "greater_than", value: "200" },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send personalized winback email with incentive",
        1000,
        "{customer_email}",
        "{winback_subject}",
        "Hi {first_name},\\n\\nWe noticed it's been a while since your last visit. {winback_message}\\n\\nAs a valued customer with {total_orders} orders, here's something special: {recommended_incentive}\\n\\nWe'd love to have you back!",
      ),
      n("6", "delayAction", "Wait 10 days for re-engagement", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Still no engagement after first winback?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "has_engaged_since_winback",
                operator: "equals",
                value: "false",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Klaviyo: Send final winback with stronger offer or sunset warning",
        1720,
        "{customer_email}",
        "We miss you, {first_name} — last chance for your exclusive offer",
        "Hi {first_name},\\n\\nThis is your final reminder before your {recommended_incentive} expires. We've also added new products we think you'll love based on your past purchases.\\n\\nIf we don't hear from you, we'll reduce email frequency to respect your inbox. Click here to stay connected!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Re-engages Klaviyo subscribers who have been inactive for 90+ days. Pulls profile activity and purchase history, uses AI to determine inactivity reason and craft a personalized winback angle. High-LTV customers receive incentive-led messaging. After a 10-day wait, still-inactive subscribers get a final stronger offer with a sunset warning to maintain list hygiene.",
  },

  // ── 52. Sunset Unengaged Subscribers (Pattern G) ──────────────────
  "Sunset Unengaged Subscribers": {
    nodes: [
      trigger("Klaviyo: 120-day no-open segment trigger", "schedule", {
        source: "klaviyo",
        schedule: "Weekly on Monday at 8:00 AM UTC",
        howItStarts:
          "Runs weekly to identify Klaviyo profiles with zero email opens in the last 120 days for sunset processing",
        setup:
          "Connect Klaviyo account. Create a segment for profiles where last email open date is more than 120 days ago. Schedule runs weekly to batch process unengaged subscribers.",
        inputFields: [],
      }),
      n("2", "hitApi", "Klaviyo: Fetch subscriber engagement metrics", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
          headers: [
            { key: "Accept", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data.attributes.email",
              customName: "subscriber_email",
              type: "string",
            },
            {
              name: "data.attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "data.attributes.properties.last_open_date",
              customName: "last_open_date",
              type: "string",
            },
            {
              name: "data.attributes.properties.total_emails_received",
              customName: "emails_received",
              type: "number",
            },
            {
              name: "data.attributes.properties.total_clicks",
              customName: "total_clicks",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI: Assess re-engagement probability and craft final appeal",
        520,
        "You are an email deliverability expert. Given subscriber engagement data, assess their re-engagement probability and write a compelling final re-engagement appeal. Return JSON with fields: reengagement_probability, appeal_subject, appeal_message, preference_center_cta.",
        "Subscriber: {first_name}, last open: {last_open_date}, emails received: {emails_received}, total clicks: {total_clicks}. Zero opens in 120+ days.",
        [
          {
            field: "reengagement_probability",
            as: "reengagement_probability",
            type: "string",
          },
          { field: "appeal_subject", as: "appeal_subject", type: "string" },
          { field: "appeal_message", as: "appeal_message", type: "string" },
          {
            field: "preference_center_cta",
            as: "preference_center_cta",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Has any click activity in last 180 days?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "total_clicks", operator: "greater_than", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Klaviyo: Send re-engagement confirmation email",
        1000,
        "{subscriber_email}",
        "{appeal_subject}",
        "Hi {first_name},\\n\\n{appeal_message}\\n\\nWe want to keep sending you content you love, but only if you want it. {preference_center_cta}\\n\\nClick below to confirm you still want to hear from us, or update your email preferences.",
      ),
      n(
        "6",
        "delayAction",
        "Wait 14 days for re-engagement confirmation",
        1240,
        {
          delayDetails: { duration: 14, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Check: Subscriber confirmed or opened email?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "confirmed_reengagement",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),
      n(
        "8",
        "hitApi",
        "Klaviyo: Suppress subscriber and update profile tag",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://a.klaviyo.com/api/profile-suppression-bulk-create-jobs/",
            headers: [
              {
                key: "Authorization",
                value: "Klaviyo-API-Key {klaviyo_private_key}",
              },
              { key: "Content-Type", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"type":"profile-suppression-bulk-create-job","attributes":{"profiles":{"data":[{"type":"profile","attributes":{"email":"{subscriber_email}"}}]}}}',
              },
            ],
            variables: [
              {
                name: "data.id",
                customName: "suppression_job_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Manages list hygiene by sunsetting Klaviyo subscribers who haven't opened an email in 120+ days. Fetches engagement metrics and uses AI to assess re-engagement probability. Sends a final re-engagement confirmation email giving the subscriber a chance to stay. After 14 days, profiles that did not confirm are suppressed via Klaviyo's suppression API to protect sender reputation and deliverability.",
  },

  // ── 53. Birthday Flow (Pattern H) ─────────────────────────────────
  "Birthday Flow": {
    nodes: [
      trigger("Klaviyo: Birthday date match trigger", "schedule", {
        source: "klaviyo",
        schedule: "Daily at 7:00 AM UTC",
        howItStarts:
          "Runs daily to identify Klaviyo profiles whose birthday matches today's date and sends a personalized birthday offer",
        setup:
          "Connect Klaviyo account. Ensure profiles have a birthday property set. Create a segment filtering for profiles where birthday month and day match today.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Klaviyo: Fetch profile loyalty data and lifetime value",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.lifetime_value",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "data.attributes.properties.vip_tier",
                customName: "vip_tier",
                type: "string",
              },
              {
                name: "data.attributes.properties.favorite_category",
                customName: "favorite_category",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "condition", "Check: Is customer VIP tier Gold or above?", 520, {
        conditionDetails: {
          conditions: [
            {
              field: "vip_tier",
              operator: "in",
              value: "gold,platinum,diamond",
            },
          ],
        },
      }),
      emailNode(
        "4",
        "Klaviyo: Send birthday celebration email with exclusive offer",
        760,
        "{customer_email}",
        "Happy Birthday, {first_name}! Here's a special gift just for you 🎂",
        "Happy Birthday, {first_name}!\\n\\nTo celebrate your special day, we've prepared an exclusive birthday reward for you. As a {vip_tier} member, enjoy an extra-special treat:\\n\\nYour Birthday Gift: 25% off your next purchase (VIP members get 30% + free shipping)\\n\\nWe've picked some items from {favorite_category} we think you'll love. Your gift is valid for the next 7 days — treat yourself!",
      ),
      n("5", "delayAction", "Wait 5 days for birthday offer redemption", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Check: Birthday offer redeemed?", 1240, {
        conditionDetails: {
          conditions: [
            {
              field: "birthday_offer_redeemed",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "7",
        "Klaviyo: Send birthday offer redemption reminder",
        1480,
        "{customer_email}",
        "{first_name}, your birthday gift expires in 2 days!",
        "Hi {first_name},\\n\\nJust a friendly reminder — your birthday discount is about to expire! You still have 2 days to redeem your exclusive {vip_tier} birthday offer.\\n\\nDon't let your special treat go to waste. Browse our latest {favorite_category} picks and save big before it's gone.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log birthday milestone and redemption status",
        1720,
        "BirthdayMilestones",
        '[["{customer_email}","{first_name}","{vip_tier}","{lifetime_value}","{birthday_offer_redeemed}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8", "output_1"),
    ],
    summary:
      "Celebrates customer birthdays with personalized Klaviyo email offers. Fetches profile loyalty data and LTV to determine VIP tier, then sends a birthday email with tier-appropriate discounts and category-specific product recommendations. After 5 days, non-redeemers get a reminder with urgency. All birthday milestones and redemption outcomes are logged to Google Sheets for loyalty program tracking.",
  },

  // ── 54. Customer Anniversary Flow (Pattern H) ─────────────────────
  "Customer Anniversary Flow": {
    nodes: [
      trigger(
        "Klaviyo: Customer first-purchase anniversary trigger",
        "schedule",
        {
          source: "klaviyo",
          schedule: "Daily at 8:00 AM UTC",
          howItStarts:
            "Runs daily to find Klaviyo profiles whose first purchase date anniversary matches today, triggering a personalized celebration",
          setup:
            "Connect Klaviyo account. Ensure profiles have a first_purchase_date property. Create a segment that matches profiles where the month and day of first_purchase_date equals today.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Klaviyo: Fetch customer tenure and purchase summary",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.first_purchase_date",
                customName: "first_purchase_date",
                type: "string",
              },
              {
                name: "data.attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "data.attributes.properties.lifetime_value",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "data.attributes.properties.loyalty_points",
                customName: "loyalty_points",
                type: "number",
              },
            ],
          },
        },
      ),
      n("3", "condition", "Check: Customer tenure 2+ years?", 520, {
        conditionDetails: {
          conditions: [
            {
              field: "years_as_customer",
              operator: "greater_than_or_equal",
              value: "2",
            },
          ],
        },
      }),
      emailNode(
        "4",
        "Klaviyo: Send anniversary celebration with loyalty reward",
        760,
        "{customer_email}",
        "Happy Anniversary, {first_name}! Celebrating your journey with us",
        "Hi {first_name},\\n\\nToday marks a special milestone — it's your customer anniversary! Since your first purchase on {first_purchase_date}, you've placed {total_orders} orders and have been an incredible part of our community.\\n\\nTo celebrate, we're giving you an exclusive anniversary reward: 20% off your next order plus bonus loyalty points. Long-time members like you (2+ years) also get a mystery bonus gift with their next purchase!\\n\\nYou currently have {loyalty_points} loyalty points. Thank you for being with us!",
      ),
      n(
        "5",
        "delayAction",
        "Wait 5 days for anniversary reward redemption",
        1000,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
      ),
      n("6", "condition", "Check: Anniversary reward redeemed?", 1240, {
        conditionDetails: {
          conditions: [
            {
              field: "anniversary_reward_redeemed",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "7",
        "Klaviyo: Send loyalty program next-level teaser",
        1480,
        "{customer_email}",
        "{first_name}, unlock more rewards — your anniversary gift is waiting",
        "Hi {first_name},\\n\\nYour anniversary reward is still waiting for you! Don't forget to use your 20% off before it expires.\\n\\nPlus, with {loyalty_points} points, you're close to unlocking the next loyalty tier. Place your anniversary order to earn double points and get even closer to exclusive perks!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log anniversary milestone and engagement",
        1720,
        "AnniversaryMilestones",
        '[["{customer_email}","{first_name}","{first_purchase_date}","{total_orders}","{lifetime_value}","{anniversary_reward_redeemed}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8", "output_1"),
    ],
    summary:
      "Celebrates the anniversary of each customer's first purchase via Klaviyo. Fetches tenure, order history, and loyalty points. Customers with 2+ years get enhanced rewards. Sends an anniversary email with a discount, bonus points, and a personalized journey recap. Non-redeemers receive a follow-up nudging them toward the next loyalty tier. All milestones are logged to Google Sheets.",
  },

  // ── 55. VIP Early Access (Pattern H) ──────────────────────────────
  "VIP Early Access": {
    nodes: [
      trigger("Klaviyo: VIP tier event or new sale launch", "webhook_app", {
        source: "klaviyo",
        event: "sale.early_access_launched",
        howItStarts:
          "Fires when a new sale or product drop is launched with VIP early access enabled, notifying qualifying VIP-tier Klaviyo profiles before the general audience",
        setup:
          "Connect Klaviyo account. Configure webhook to fire when a sale or collection is published with an early access window. The workflow filters for VIP-tier profiles.",
        inputFields: [],
      }),
      n("2", "hitApi", "Klaviyo: Fetch VIP profile and tier details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
          headers: [
            { key: "Accept", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data.attributes.email",
              customName: "vip_email",
              type: "string",
            },
            {
              name: "data.attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "data.attributes.properties.vip_tier",
              customName: "vip_tier",
              type: "string",
            },
            {
              name: "data.attributes.properties.lifetime_value",
              customName: "lifetime_value",
              type: "number",
            },
            {
              name: "data.attributes.properties.favorite_category",
              customName: "favorite_category",
              type: "string",
            },
          ],
        },
      }),
      n("3", "condition", "Check: VIP tier is Platinum or Diamond?", 520, {
        conditionDetails: {
          conditions: [
            { field: "vip_tier", operator: "in", value: "platinum,diamond" },
          ],
        },
      }),
      emailNode(
        "4",
        "Klaviyo: Send VIP early access notification",
        760,
        "{vip_email}",
        "{first_name}, your VIP early access is live — shop before everyone else",
        "Hi {first_name},\\n\\nAs a {vip_tier} VIP member, you get exclusive early access to our newest drop before it opens to the public.\\n\\nYour early access window: 48 hours before general release\\nExclusive VIP perk: Free shipping + extra 10% off\\n\\nWe've curated picks from {favorite_category} based on your preferences. Shop now to secure your favorites before they sell out!",
      ),
      n("5", "delayAction", "Wait 6 days for VIP purchase activity", 1000, {
        delayDetails: { duration: 6, unit: "days" },
      }),
      n(
        "6",
        "condition",
        "Check: VIP made a purchase during early access?",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                field: "early_access_purchased",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Klaviyo: Send exclusive VIP follow-up with next drop preview",
        1480,
        "{vip_email}",
        "{first_name}, exclusive preview of what's coming next",
        "Hi {first_name},\\n\\nThank you for being one of our most valued {vip_tier} members! Whether you shopped the early access or not, we wanted to give you a sneak peek at what's coming next.\\n\\nAs a {vip_tier} member with ${lifetime_value} in lifetime purchases, you'll always be first in line. Stay tuned for your next exclusive drop notification!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log VIP early access engagement",
        1720,
        "VIPEarlyAccess",
        '[["{vip_email}","{first_name}","{vip_tier}","{lifetime_value}","{early_access_purchased}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
    summary:
      "Gives Klaviyo VIP-tier customers early access to new sales and product drops. Fetches profile tier and preferences, then sends a 48-hour early access notification with VIP-exclusive perks like free shipping and extra discounts. Platinum and Diamond members get enhanced offers. After 6 days, all VIPs receive a next-drop preview to maintain engagement. Results are logged to Google Sheets.",
  },

  // ── 56. Loyalty Points Expiration (Pattern H) ─────────────────────
  "Loyalty Points Expiration": {
    nodes: [
      trigger("Klaviyo: Loyalty points expiration approaching", "schedule", {
        source: "klaviyo",
        schedule: "Daily at 9:00 AM UTC",
        howItStarts:
          "Runs daily to identify Klaviyo profiles with loyalty points expiring within the next 14 days and sends timely reminders to encourage redemption",
        setup:
          "Connect Klaviyo account. Create a segment for profiles where points_expiration_date is within 14 days from today and points_balance is greater than 0.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Klaviyo: Fetch loyalty points and expiration details",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.loyalty_points",
                customName: "points_balance",
                type: "number",
              },
              {
                name: "data.attributes.properties.points_expiration_date",
                customName: "expiration_date",
                type: "string",
              },
              {
                name: "data.attributes.properties.vip_tier",
                customName: "vip_tier",
                type: "string",
              },
              {
                name: "data.attributes.properties.points_value_dollars",
                customName: "points_dollar_value",
                type: "number",
              },
            ],
          },
        },
      ),
      n("3", "condition", "Check: Points balance worth more than $10?", 520, {
        conditionDetails: {
          conditions: [
            {
              field: "points_dollar_value",
              operator: "greater_than",
              value: "10",
            },
          ],
        },
      }),
      emailNode(
        "4",
        "Klaviyo: Send points expiration warning with redemption options",
        760,
        "{customer_email}",
        "{first_name}, your {points_balance} loyalty points expire soon!",
        "Hi {first_name},\\n\\nYou have {points_balance} loyalty points (worth ${points_dollar_value}) that will expire on {expiration_date}!\\n\\nDon't let your hard-earned rewards go to waste. Here are easy ways to redeem:\\n• Apply points to your next purchase for instant savings\\n• Redeem for a gift card\\n• Donate points to a charity partner\\n\\nAs a {vip_tier} member, you've earned these points through your loyalty — use them before they're gone!",
      ),
      n("5", "delayAction", "Wait 7 days before final reminder", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("6", "condition", "Check: Points still unredeemed?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "points_redeemed", operator: "equals", value: "false" },
          ],
        },
      }),
      emailNode(
        "7",
        "Klaviyo: Send last-chance points expiration reminder",
        1480,
        "{customer_email}",
        "Last chance, {first_name}! {points_balance} points expire in days",
        "Hi {first_name},\\n\\nThis is your final reminder — {points_balance} loyalty points (${points_dollar_value} value) expire on {expiration_date}. That's just days away!\\n\\nWe'd hate to see you lose these rewards. Use them now on any purchase in our store.\\n\\nQuick tip: Even a small purchase lets you apply your points for maximum savings!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log points expiration and redemption tracking",
        1720,
        "PointsExpiration",
        '[["{customer_email}","{first_name}","{points_balance}","{points_dollar_value}","{expiration_date}","{points_redeemed}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
    summary:
      "Prevents loyalty point loss by alerting Klaviyo subscribers when their points are about to expire. Fetches points balance, dollar value, and expiration date. Sends a detailed email with multiple redemption options. High-value balances get priority messaging. After 7 days, still-unredeemed subscribers receive a last-chance urgency email. Outcomes are logged to Google Sheets for loyalty program analysis.",
  },

  // ── 57. Wishlist Reminder (Pattern D) ─────────────────────────────
  "Wishlist Reminder": {
    nodes: [
      trigger(
        "Klaviyo: Wishlist item price drop or back-in-stock event",
        "webhook_app",
        {
          source: "klaviyo",
          event: "wishlist.item_updated",
          howItStarts:
            "Fires when a wishlisted item has a price drop, comes back in stock, or has low inventory, alerting the Klaviyo subscriber who wishlisted it",
          setup:
            "Connect Klaviyo account and ecommerce platform. Configure webhook to fire on price decrease, restock, or low inventory events for items in customer wishlists.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract wishlist item details and change type",
        280,
        "You are an ecommerce data parser. Given a wishlist item update event, extract the product details and determine the type of change. Return JSON with fields: product_name, product_url, original_price, current_price, price_drop_amount, change_type, stock_status, urgency_level.",
        "Wishlist item updated event payload: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "original_price", as: "original_price", type: "number" },
          { field: "current_price", as: "current_price", type: "number" },
          {
            field: "price_drop_amount",
            as: "price_drop_amount",
            type: "number",
          },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Verify subscriber interest and email consent",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.email_consent",
                customName: "email_consent",
                type: "boolean",
              },
              {
                name: "data.attributes.properties.wishlist_count",
                customName: "wishlist_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: High urgency — price drop > 20% or stock < 10?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "urgency_level", operator: "equals", value: "high" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Klaviyo: Send wishlist item alert with deep link",
        1000,
        "{customer_email}",
        "{first_name}, your wishlisted {product_name} just got better!",
        "Hi {first_name},\\n\\nGreat news about an item on your wishlist!\\n\\n{product_name}\\nChange: {change_type}\\nOriginal price: ${original_price} → Now: ${current_price} (save ${price_drop_amount})\\n\\nThis item is seeing high demand — don't wait too long! You have {wishlist_count} items on your wishlist, and this one is ready for you now.\\n\\nShop now: {product_url}",
      ),
      n("6", "delayAction", "Wait 2 days for purchase action", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Item still available and not yet purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "wishlist_item_purchased",
                operator: "equals",
                value: "false",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Klaviyo: Send urgency follow-up with similar items",
        1720,
        "{customer_email}",
        "Still thinking about {product_name}? It might not last, {first_name}",
        "Hi {first_name},\\n\\n{product_name} is still available at ${current_price}, but stock is moving fast.\\n\\nIf it sells out, here are similar items you might love based on your wishlist preferences.\\n\\nDon't miss this deal — wishlisted items at this price tend to go quickly!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Alerts Klaviyo subscribers when their wishlisted items have a price drop, restock, or low inventory. AI extracts product details and urgency level from the event payload. Verifies subscriber consent before sending a personalized alert email with deep links. After 2 days, non-purchasers get an urgency follow-up with similar product recommendations to drive conversion.",
  },

  // ── 58. New Collection Launch (Pattern D) ─────────────────────────
  "New Collection Launch": {
    nodes: [
      trigger("Klaviyo: New collection published event", "webhook_app", {
        source: "klaviyo",
        event: "collection.published",
        howItStarts:
          "Fires when a new product collection is published in the ecommerce platform, triggering category-matched announcements to interested Klaviyo subscribers",
        setup:
          "Connect Klaviyo account and ecommerce platform. Configure webhook to fire when a new collection or product category is published. The workflow matches subscribers by category preference.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract collection details and target audience",
        280,
        "You are a merchandising strategist. Given a new collection launch event, extract the collection details and craft an engaging announcement. Return JSON with fields: collection_name, collection_url, hero_product, product_count, price_range, target_audience_description, announcement_headline, announcement_body.",
        "New collection published event: {webhook_payload}",
        [
          { field: "collection_name", as: "collection_name", type: "string" },
          { field: "collection_url", as: "collection_url", type: "string" },
          { field: "hero_product", as: "hero_product", type: "string" },
          { field: "product_count", as: "product_count", type: "number" },
          { field: "price_range", as: "price_range", type: "string" },
          {
            field: "announcement_headline",
            as: "announcement_headline",
            type: "string",
          },
          {
            field: "announcement_body",
            as: "announcement_body",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "Klaviyo: Lookup subscriber category preferences", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
          headers: [
            { key: "Accept", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data.attributes.email",
              customName: "subscriber_email",
              type: "string",
            },
            {
              name: "data.attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "data.attributes.properties.preferred_categories",
              customName: "preferred_categories",
              type: "string",
            },
            {
              name: "data.attributes.properties.last_purchase_category",
              customName: "last_purchase_category",
              type: "string",
            },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Check: Subscriber's preferred category matches collection?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "category_match", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Klaviyo: Send new collection announcement email",
        1000,
        "{subscriber_email}",
        "{announcement_headline}",
        "Hi {first_name},\\n\\n{announcement_body}\\n\\nIntroducing our {collection_name} — {product_count} new pieces starting at {price_range}.\\n\\nFeatured: {hero_product}\\n\\nBased on your love for {preferred_categories}, we think you'll find something perfect. Be among the first to shop!\\n\\nExplore now: {collection_url}",
      ),
      n("6", "delayAction", "Wait 3 days for collection engagement", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Subscriber browsed or purchased from collection?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "collection_engagement",
                operator: "equals",
                value: "false",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Klaviyo: Send collection follow-up with best sellers",
        1720,
        "{subscriber_email}",
        "{first_name}, see what's trending from {collection_name}",
        "Hi {first_name},\\n\\nOur {collection_name} is getting great response! Here are the early best-sellers your fellow shoppers are loving.\\n\\nTop picks from {collection_name}:\\n• {hero_product} — already a customer favorite\\n• More items curated for your style\\n\\nDon't wait too long — popular sizes and colors are moving fast!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Announces new product collections to category-matched Klaviyo subscribers. AI extracts collection details and crafts an engaging announcement from the publish event. Subscribers are matched by their preferred and past purchase categories for relevance. After 3 days, non-engaged subscribers receive a follow-up highlighting early best-sellers to drive urgency and social proof.",
  },

  // ── 59. Gift Guide Journey (Pattern I) ─────────────────────────────
  "Gift Guide Journey": {
    nodes: [
      trigger("Klaviyo: Holiday season gift guide launch", "webhook_app", {
        source: "klaviyo",
        event: "campaign.gift_guide_published",
        howItStarts:
          "Fires when a holiday gift guide is published, sending curated gift recommendations to Klaviyo subscribers based on their purchase history and recipient preferences",
        setup:
          "Connect Klaviyo account. Configure webhook to fire when a holiday gift guide campaign is published. The workflow segments subscribers by purchase history and engagement level.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract gift guide theme and curate category picks",
        280,
        "You are a holiday marketing strategist. Given a gift guide publication event, extract the theme and curate category recommendations. Return JSON with fields: guide_theme, holiday_name, guide_url, top_gift_categories, price_tiers, gift_for_segments, urgency_note.",
        "Gift guide publication event: {webhook_payload}",
        [
          { field: "guide_theme", as: "guide_theme", type: "string" },
          { field: "holiday_name", as: "holiday_name", type: "string" },
          { field: "guide_url", as: "guide_url", type: "string" },
          {
            field: "top_gift_categories",
            as: "top_gift_categories",
            type: "string",
          },
          { field: "price_tiers", as: "price_tiers", type: "string" },
          {
            field: "gift_for_segments",
            as: "gift_for_segments",
            type: "string",
          },
          { field: "urgency_note", as: "urgency_note", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Fetch subscriber lifecycle stage and purchase history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "subscriber_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.avg_order_value",
                customName: "avg_order_value",
                type: "number",
              },
              {
                name: "data.attributes.properties.purchase_categories",
                customName: "purchase_categories",
                type: "string",
              },
              {
                name: "data.attributes.properties.last_purchase_date",
                customName: "last_purchase_date",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Subscriber is an active buyer (purchased in last 90 days)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "days_since_last_purchase",
                operator: "less_than",
                value: "90",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Klaviyo: Send personalized gift guide with curated picks",
        1000,
        "{subscriber_email}",
        "{first_name}, your {holiday_name} gift guide is here!",
        "Hi {first_name},\\n\\nThe {holiday_name} season is here, and we've curated the perfect gift guide to make your shopping easy!\\n\\n{guide_theme}\\n\\nBased on your past purchases in {purchase_categories}, here are our top picks:\\n\\nGift ideas by budget: {price_tiers}\\nPerfect for: {gift_for_segments}\\nTop categories: {top_gift_categories}\\n\\n{urgency_note}\\n\\nExplore the full guide: {guide_url}",
      ),
      n("6", "delayAction", "Wait 4 days for gift guide engagement", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Subscriber opened guide or made a purchase?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "guide_engaged", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Klaviyo: Send category-specific gift picks follow-up",
        1720,
        "{subscriber_email}",
        "Top {holiday_name} gifts in {purchase_categories} — curated for you, {first_name}",
        "Hi {first_name},\\n\\nStill looking for the perfect gift? We've narrowed it down to our best-sellers in {purchase_categories} — categories you know and love.\\n\\nThese are flying off the shelves this {holiday_name} season. {urgency_note}\\n\\nShop curated picks: {guide_url}\\n\\nNeed help? Our gift concierge team is here to assist!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Delivers personalized holiday gift guide emails to Klaviyo subscribers when a gift guide is published. AI extracts the guide theme, categories, and price tiers. Subscribers are segmented by recent purchase activity and purchase categories for relevance. Active buyers receive curated picks matching their shopping history. Non-engaged subscribers get a category-specific follow-up with best-seller social proof after 4 days.",
  },

  // ── 60. Predictive Next Order Reminder (Pattern F) ────────────────
  "Predictive Next Order Reminder": {
    nodes: [
      trigger("Klaviyo: Predicted reorder date approaching", "schedule", {
        source: "klaviyo",
        schedule: "Daily at 10:00 AM UTC",
        howItStarts:
          "Runs daily to identify Klaviyo profiles whose predicted next order date is within 5 days, based on purchase frequency analysis",
        setup:
          "Connect Klaviyo account. Ensure profiles have predicted_next_order_date calculated from purchase frequency. Create a segment where predicted_next_order_date is within 5 days from today.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Analyze purchase pattern and predict reorder needs",
        280,
        "You are a predictive commerce analyst. Given a customer purchase history, identify the most likely products they need to reorder and the optimal timing. Return JSON with fields: predicted_products, reorder_urgency, personalized_subject, personalized_message, cross_sell_suggestion.",
        "Customer purchase history: last order items: {last_order_items}, average order frequency: {avg_order_frequency_days} days, last order date: {last_order_date}, total orders: {total_orders}",
        [
          {
            field: "predicted_products",
            as: "predicted_products",
            type: "string",
          },
          { field: "reorder_urgency", as: "reorder_urgency", type: "string" },
          {
            field: "personalized_subject",
            as: "personalized_subject",
            type: "string",
          },
          {
            field: "personalized_message",
            as: "personalized_message",
            type: "string",
          },
          {
            field: "cross_sell_suggestion",
            as: "cross_sell_suggestion",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Check payment method and subscription status",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.has_active_subscription",
                customName: "has_subscription",
                type: "boolean",
              },
              {
                name: "data.attributes.properties.avg_order_value",
                customName: "avg_order_value",
                type: "number",
              },
              {
                name: "data.attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Customer has no active subscription (needs manual reorder)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "has_subscription", operator: "equals", value: "false" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Klaviyo: Send smart reorder reminder with predicted products",
        1000,
        "{customer_email}",
        "{personalized_subject}",
        "Hi {first_name},\\n\\n{personalized_message}\\n\\nBased on your purchase pattern ({total_orders} orders), it looks like you might be running low on:\\n\\n{predicted_products}\\n\\nReorder now and get these delivered right when you need them. {reorder_urgency}\\n\\nYou might also like: {cross_sell_suggestion}\\n\\nPro tip: Set up a subscription to never run out and save 15% on every order!",
      ),
      n("6", "delayAction", "Wait 4 days for reorder action", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n("7", "condition", "Check: Customer has not reordered yet?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "reorder_placed", operator: "equals", value: "false" },
          ],
        },
      }),
      emailNode(
        "8",
        "Klaviyo: Send reorder follow-up with subscription offer",
        1720,
        "{customer_email}",
        "{first_name}, don't run out — quick reorder reminder",
        "Hi {first_name},\\n\\nJust a quick reminder — based on your usual ordering pattern, now is the perfect time to restock.\\n\\n{predicted_products}\\n\\nReorder in one click, or subscribe to save 15% and get automatic deliveries timed to when you need them.\\n\\nFree shipping on all reorders over $35!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Uses predictive analytics to remind Klaviyo customers to reorder before they run out. AI analyzes purchase patterns to identify likely reorder products and optimal timing. Non-subscription customers receive a smart reminder with predicted products and a subscription upsell. After 4 days, non-reorderers get a follow-up emphasizing convenience and the subscription savings offer.",
  },

  // ── 61. Referral Ask After Positive Review (Pattern J) ────────────
  "Referral Ask After Positive Review": {
    nodes: [
      trigger("Klaviyo: Positive product review submitted", "webhook_app", {
        source: "klaviyo",
        event: "review.submitted",
        howItStarts:
          "Fires when a Klaviyo subscriber submits a product review with a 4 or 5-star rating, indicating satisfaction suitable for a referral ask",
        setup:
          "Connect Klaviyo account and review platform (e.g., Yotpo, Judge.me, Stamped). Configure webhook to fire when a review is submitted with a rating of 4 or 5 stars.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Klaviyo: Lookup reviewer profile and prior advocacy history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "reviewer_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "data.attributes.properties.last_referral_ask_date",
                customName: "last_referral_ask_date",
                type: "string",
              },
              {
                name: "data.attributes.properties.referral_code",
                customName: "referral_code",
                type: "string",
              },
              {
                name: "data.attributes.properties.total_referrals",
                customName: "total_referrals",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Not asked for referral in last 60 days?",
        520,
        {
          conditionDetails: {
            conditions: [
              {
                field: "days_since_last_referral_ask",
                operator: "greater_than",
                value: "60",
              },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Klaviyo: Send referral ask with personalized incentive",
        760,
        "{reviewer_email}",
        "Thanks for the amazing review, {first_name}! Share the love and earn rewards",
        "Hi {first_name},\\n\\nThank you for your wonderful review! We're thrilled you're enjoying your purchase.\\n\\nSince you had such a great experience, we'd love for you to share it with friends and family. For every friend who makes a purchase using your referral code, you both get rewarded:\\n\\nYour referral code: {referral_code}\\nYour reward: $15 store credit per referral\\nTheir reward: $10 off their first order\\n\\nYou've already referred {total_referrals} friends — keep the streak going!\\n\\nShare your code now and start earning.",
      ),
      n("5", "delayAction", "Wait 6 days for referral activity", 1000, {
        delayDetails: { duration: 6, unit: "days" },
      }),
      n(
        "6",
        "condition",
        "Check: Referral link shared or conversion made?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { field: "referral_shared", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Klaviyo: Send referral reminder with social sharing options",
        1480,
        "{reviewer_email}",
        "{first_name}, your $15 referral reward is waiting — share with one friend!",
        "Hi {first_name},\\n\\nJust a quick reminder — you can earn $15 in store credit for every friend who shops with your code: {referral_code}\\n\\nSharing is easy:\\n• Forward this email to a friend\\n• Share your code on social media\\n• Text your code directly\\n\\nWith {total_orders} orders, you clearly know good products — help your friends discover them too!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log referral ask and conversion status",
        1720,
        "ReferralTracking",
        '[["{reviewer_email}","{first_name}","{referral_code}","{total_referrals}","{referral_shared}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8", "output_1"),
    ],
    summary:
      "Capitalizes on positive reviews by asking satisfied Klaviyo customers for referrals. Checks advocacy history to avoid ask fatigue (60-day cooldown). Sends a referral email with a personalized code and dual-sided incentive ($15 for referrer, $10 for friend). After 6 days, non-sharers get a reminder with easy social sharing options. All referral activity is logged to Google Sheets for program tracking.",
  },

  // ── 62. Coupon Expiry Reminder (Pattern D) ────────────────────────
  "Coupon Expiry Reminder": {
    nodes: [
      trigger("Klaviyo: Coupon expiration approaching event", "webhook_app", {
        source: "klaviyo",
        event: "coupon.expiring_soon",
        howItStarts:
          "Fires when an assigned coupon code is within 3 days of expiring, reminding the Klaviyo subscriber to use it before it expires",
        setup:
          "Connect Klaviyo account. Configure webhook to fire when a coupon assigned to a profile has an expiration date within 3 days. Include coupon code, discount value, and expiration date in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract coupon details and craft urgency messaging",
        280,
        "You are a conversion copywriter specializing in urgency-driven ecommerce messaging. Given a coupon expiration event, extract coupon details and write compelling reminder copy. Return JSON with fields: coupon_code, discount_value, discount_type, expiration_date, days_remaining, urgency_subject, urgency_message, product_suggestion_angle.",
        "Coupon expiration event payload: {webhook_payload}",
        [
          { field: "coupon_code", as: "coupon_code", type: "string" },
          { field: "discount_value", as: "discount_value", type: "string" },
          { field: "discount_type", as: "discount_type", type: "string" },
          { field: "expiration_date", as: "expiration_date", type: "string" },
          { field: "days_remaining", as: "days_remaining", type: "number" },
          { field: "urgency_subject", as: "urgency_subject", type: "string" },
          { field: "urgency_message", as: "urgency_message", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Verify subscriber profile and recent purchase activity",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.favorite_category",
                customName: "favorite_category",
                type: "string",
              },
              {
                name: "data.attributes.properties.last_browse_category",
                customName: "last_browse_category",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Coupon expires within 1 day (critical urgency)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "days_remaining",
                operator: "less_than_or_equal",
                value: "1",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Klaviyo: Send coupon expiry reminder with product picks",
        1000,
        "{customer_email}",
        "{urgency_subject}",
        "Hi {first_name},\\n\\n{urgency_message}\\n\\nYour coupon: {coupon_code}\\nDiscount: {discount_value} {discount_type}\\nExpires: {expiration_date} ({days_remaining} days left!)\\n\\nHere are some picks from {favorite_category} to inspire your purchase. Don't let this discount go to waste!\\n\\nApply code {coupon_code} at checkout.",
      ),
      n("6", "delayAction", "Wait 1 day before final reminder", 1240, {
        delayDetails: { duration: 1, unit: "days" },
      }),
      n("7", "condition", "Check: Coupon still unused and not expired?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "coupon_redeemed", operator: "equals", value: "false" },
          ],
        },
      }),
      emailNode(
        "8",
        "Klaviyo: Send last-chance coupon expiry alert",
        1720,
        "{customer_email}",
        "LAST CHANCE: {first_name}, your {discount_value} coupon expires today!",
        "Hi {first_name},\\n\\nThis is it — your {discount_value} {discount_type} coupon ({coupon_code}) expires TODAY!\\n\\nHere's what you could save on trending items in {last_browse_category} and {favorite_category}.\\n\\nOnce it's gone, it's gone. Use code {coupon_code} at checkout now!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Reminds Klaviyo subscribers about expiring coupon codes to prevent missed savings and drive urgency-based conversions. AI extracts coupon details and crafts time-sensitive messaging. Sends a reminder with category-specific product suggestions and clear coupon instructions. After 1 day, unredeemed coupons trigger a final last-chance alert emphasizing same-day expiration urgency.",
  },

  // ── 63. Browse-to-Cart Conversion Push (Pattern C) ────────────────
  "Browse-to-Cart Conversion Push": {
    nodes: [
      trigger(
        "Klaviyo: Multiple product browse sessions detected",
        "webhook_app",
        {
          source: "klaviyo",
          event: "browsing.high_intent_detected",
          howItStarts:
            "Fires when a Klaviyo subscriber has viewed 3+ products across 2+ sessions without adding anything to cart, indicating high browse intent but conversion friction",
          setup:
            "Connect Klaviyo account and ecommerce platform. Configure webhook to fire when a profile hits the multi-browse threshold: 3+ products viewed across 2+ sessions with no cart add in the last 7 days.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract browsed products and identify conversion barriers",
        280,
        "You are a conversion optimization expert. Given a high-intent browser's activity, identify the most viewed products, likely conversion barriers, and craft a personalized nudge. Return JSON with fields: top_viewed_products, browse_count, likely_barrier, conversion_nudge_subject, conversion_nudge_message, recommended_incentive.",
        "Browser activity: {webhook_payload}. Products viewed across multiple sessions without cart add.",
        [
          {
            field: "top_viewed_products",
            as: "top_viewed_products",
            type: "string",
          },
          { field: "browse_count", as: "browse_count", type: "number" },
          { field: "likely_barrier", as: "likely_barrier", type: "string" },
          {
            field: "conversion_nudge_subject",
            as: "conversion_nudge_subject",
            type: "string",
          },
          {
            field: "conversion_nudge_message",
            as: "conversion_nudge_message",
            type: "string",
          },
          {
            field: "recommended_incentive",
            as: "recommended_incentive",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Fetch customer affinity and purchase history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/v2/person/{person_id}?api_key={klaviyo_private_key}",
            headers: [
              { key: "Accept", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data.attributes.email",
                customName: "browser_email",
                type: "string",
              },
              {
                name: "data.attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data.attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "data.attributes.properties.preferred_categories",
                customName: "preferred_categories",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Returning customer with previous purchases?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "total_orders", operator: "greater_than", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Klaviyo: Send browse-to-cart conversion email with viewed products",
        1000,
        "{browser_email}",
        "{conversion_nudge_subject}",
        "Hi {first_name},\\n\\n{conversion_nudge_message}\\n\\nWe noticed you've been eyeing some great items ({browse_count} products viewed). Here are the ones that caught your attention most:\\n\\n{top_viewed_products}\\n\\nNeed a little push? {recommended_incentive}\\n\\nAdd them to your cart now and check out before they sell out!",
      ),
      n("6", "delayAction", "Wait 1 day for cart add activity", 1240, {
        delayDetails: { duration: 1, unit: "days" },
      }),
      n("7", "condition", "Check: Still no cart add after nudge?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "cart_item_added", operator: "equals", value: "false" },
          ],
        },
      }),
      emailNode(
        "8",
        "Klaviyo: Send category recommendations with social proof",
        1720,
        "{browser_email}",
        "{first_name}, shoppers who browsed these items also loved...",
        "Hi {first_name},\\n\\nStill deciding? Here's some inspiration — shoppers who viewed the same items you did ended up loving these best-sellers in {preferred_categories}.\\n\\nOur most popular picks right now include items that match your browsing style. Plus, we have free returns so you can shop with confidence.\\n\\nExplore curated picks just for you and find your perfect match!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Converts high-intent browsers into buyers by targeting Klaviyo subscribers who have viewed 3+ products across multiple sessions without adding to cart. AI identifies top viewed products and likely conversion barriers. Sends a personalized nudge with browsed items and a targeted incentive. After 1 day, non-converters receive a social proof-driven follow-up with collaborative filtering recommendations and a free returns reassurance.",
  },
};
